import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Inline qty + buy row for product cards (variant select + cart hints).
 *
 * @extends Component
 */
class ProductCardInlineBuy extends Component {
  /** @type {(event: Event) => void} */
  #boundCartUpdate;

  /** @type {(event: Event) => void} */
  #boundVariantUpdate;

  /** @type {Cart | null} */
  #lastCart = null;

  connectedCallback() {
    super.connectedCallback();
    this.#boundCartUpdate = this.#handleCartUpdate.bind(this);
    this.#boundVariantUpdate = this.#handleVariantUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#boundCartUpdate);
    document.addEventListener(ThemeEvents.variantUpdate, this.#boundVariantUpdate, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#boundCartUpdate);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#boundVariantUpdate, { capture: true });
  }

  /**
   * @param {Event} event
   */
  onVariantChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const row = select.closest('product-form-component');
    if (!(row instanceof HTMLElement)) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    const available = option.dataset.available === 'true';
    const variantTitle = this.#getVariantTitleFromOption(option);

    row.dataset.variantTitle = variantTitle;

    const hiddenVariantId = row.querySelector('input[ref="variantId"]');
    if (hiddenVariantId instanceof HTMLInputElement) {
      hiddenVariantId.value = select.value;
      hiddenVariantId.disabled = !available;
    }

    const cartQty = parseInt(option.dataset.cartQuantity || '0', 10);
    this.#updateRowCartState(row, select.value, cartQty, available, variantTitle);

    const qtySelector = row.querySelector('quantity-selector-component');
    const qtyInput = row.querySelector('input[name="quantity"]');
    if (qtyInput instanceof HTMLInputElement) {
      const qtyMin = parseInt(option.dataset.qtyMin || '1', 10);
      const qtyIncrement = parseInt(option.dataset.qtyIncrement || '1', 10);
      const qtyMax = option.dataset.qtyMax ? parseInt(option.dataset.qtyMax, 10) : null;

      qtyInput.min = String(qtyMin);
      qtyInput.step = String(qtyIncrement);
      qtyInput.setAttribute('data-min', String(qtyMin));

      if (qtyMax !== null) {
        qtyInput.max = String(qtyMax);
      } else {
        qtyInput.removeAttribute('max');
      }

      if (cartQty === 0) {
        qtyInput.value = String(qtyMin);
      }

      if (qtySelector && 'updateConstraints' in qtySelector && typeof qtySelector.updateConstraints === 'function') {
        qtySelector.updateConstraints(String(qtyMin), qtyMax !== null ? String(qtyMax) : null, String(qtyIncrement));
      }
    }

    this.#scheduleRowAddButtonSync(row, available);
  }

  /**
   * @param {Event} event
   */
  #handleCartUpdate(event) {
    const cart = event.detail?.resource;
    if (!cart?.items) return;

    this.#lastCart = cart;

    for (const formWrap of this.querySelectorAll('product-form-component')) {
      if (!(formWrap instanceof HTMLElement)) continue;

      const variantId = this.#getRowVariantId(formWrap);
      if (!variantId) continue;

      const cartQty = this.#getCartQtyForVariant(cart, variantId);
      const available = this.#isRowVariantAvailable(formWrap, variantId);
      this.#updateRowCartState(formWrap, variantId, cartQty, available);

      const select = formWrap.querySelector('.product-card-inline-buy__variant-select');
      if (select instanceof HTMLSelectElement) {
        for (const option of select.options) {
          option.dataset.cartQuantity = String(this.#getCartQtyForVariant(cart, option.value));
        }
      }
    }
  }

  /**
   * Sync in-cart hint when variant changes via swatches (product-card stops bubble).
   * @param {Event} event
   */
  #handleVariantUpdate(event) {
    const formWrap = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (!(formWrap instanceof HTMLElement) || !this.contains(formWrap)) return;

    const productId = formWrap.dataset.productId;
    if (productId && event.detail?.data?.productId && event.detail.data.productId !== productId) return;

    const resource = event.detail?.resource;
    const variantId = resource?.id != null ? String(resource.id) : this.#getRowVariantId(formWrap);
    if (!variantId) return;

    const select = formWrap.querySelector('.product-card-inline-buy__variant-select');
    if (select instanceof HTMLSelectElement && select.value !== variantId) {
      select.value = variantId;
    }

    const cartQty = this.#lastCart
      ? this.#getCartQtyForVariant(this.#lastCart, variantId)
      : parseInt(
          select instanceof HTMLSelectElement
            ? select.options[select.selectedIndex]?.dataset.cartQuantity || '0'
            : '0',
          10
        );

    const available = resource?.available !== false && this.#isRowVariantAvailable(formWrap, variantId);
    this.#updateRowCartState(formWrap, variantId, cartQty, available);
  }

  /**
   * @param {HTMLElement} row
   * @returns {string}
   */
  #getRowVariantId(row) {
    const hidden = row.querySelector('input[ref="variantId"]');
    if (hidden instanceof HTMLInputElement && hidden.value) return hidden.value;

    const select = row.querySelector('.product-card-inline-buy__variant-select');
    if (select instanceof HTMLSelectElement) return select.value;

    return '';
  }

  /**
   * @param {HTMLOptionElement} option
   * @returns {string}
   */
  #getVariantTitleFromOption(option) {
    if (option.dataset.variantTitle) {
      return option.dataset.variantTitle;
    }

    const soldOutSep = ' — ';
    const text = option.textContent || '';
    const sepIndex = text.indexOf(soldOutSep);
    return sepIndex >= 0 ? text.slice(0, sepIndex).trim() : text.trim();
  }

  /**
   * @param {HTMLElement} row
   * @param {string} variantId
   * @returns {string}
   */
  #getVariantTitleFromRow(row, variantId) {
    const select = row.querySelector('.product-card-inline-buy__variant-select');
    if (select instanceof HTMLSelectElement) {
      const option = Array.from(select.options).find((opt) => opt.value === String(variantId));
      if (option) return this.#getVariantTitleFromOption(option);
    }

    return row.dataset.variantTitle || '';
  }

  /**
   * @param {Cart} cart
   * @param {string} variantId
   * @returns {number}
   */
  #getCartQtyForVariant(cart, variantId) {
    const item = cart.items.find((line) => String(line.variant_id) === String(variantId));
    return item ? item.quantity : 0;
  }

  /**
   * @param {HTMLElement} row
   * @param {string} variantId
   * @returns {boolean}
   */
  #isRowVariantAvailable(row, variantId) {
    const select = row.querySelector('.product-card-inline-buy__variant-select');
    if (select instanceof HTMLSelectElement) {
      const option = Array.from(select.options).find((opt) => opt.value === String(variantId));
      return option?.dataset.available === 'true';
    }

    const soldOutHint = row.querySelector('[ref="qtySoldOutHint"]');
    return !(soldOutHint instanceof HTMLElement) || soldOutHint.hidden;
  }

  /**
   * @param {HTMLElement} row
   * @param {string} variantId
   * @param {number} cartQty
   * @param {boolean} available
   * @param {string} [variantTitle]
   */
  #updateRowCartState(row, variantId, cartQty, available, variantTitle) {
    const title = variantTitle ?? this.#getVariantTitleFromRow(row, variantId);
    if (title) {
      row.dataset.variantTitle = title;
    }

    const qtySelector = row.querySelector('quantity-selector-component');
    if (qtySelector instanceof HTMLElement) {
      qtySelector.dataset.variantId = variantId;
    }
    if (qtySelector && 'setCartQuantity' in qtySelector && typeof qtySelector.setCartQuantity === 'function') {
      qtySelector.setCartQuantity(cartQty);
    }

    const inCartHint = row.querySelector('[ref="qtyInCartHint"]');
    if (inCartHint instanceof HTMLElement) {
      inCartHint.hidden = cartQty === 0;
      if (cartQty > 0) {
        inCartHint.textContent = this.#formatInCartHint(cartQty, title);
      }
    }

    const qtyControl = row.querySelector('[ref="qtyControl"]');
    const soldOutHint = row.querySelector('[ref="qtySoldOutHint"]');
    if (qtyControl instanceof HTMLElement) {
      qtyControl.hidden = !available;
    }
    if (soldOutHint instanceof HTMLElement) {
      soldOutHint.hidden = available;
    }

    const qtyInput = row.querySelector('input[name="quantity"]');
    if (qtyInput instanceof HTMLInputElement) {
      qtyInput.disabled = !available;
    }

    if (qtySelector && 'updateButtonStates' in qtySelector && typeof qtySelector.updateButtonStates === 'function') {
      qtySelector.updateButtonStates();
    }

    this.#scheduleRowAddButtonSync(row, available);
  }

  /**
   * @param {number} cartQty
   * @param {string} variantTitle
   * @returns {string}
   */
  #formatInCartHint(cartQty, variantTitle) {
    const label = this.dataset.inCartLabel || 'in cart';
    const qtyLabel = `${cartQty} ${label}`;
    return variantTitle ? `${variantTitle} (${qtyLabel})` : qtyLabel;
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} [available]
   */
  #scheduleRowAddButtonSync(row, available) {
    queueMicrotask(() => {
      this.#updateAddButtonState(row, available);
    });
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} [available]
   */
  #updateAddButtonState(row, available) {
    const soldOutHint = row.querySelector('[ref="qtySoldOutHint"]');
    const rowAvailable =
      available !== undefined
        ? available
        : !(soldOutHint instanceof HTMLElement && !soldOutHint.hidden);

    for (const container of row.querySelectorAll('add-to-cart-component')) {
      if ('enable' in container && 'disable' in container) {
        if (rowAvailable) {
          /** @type {{ enable: () => void }} */ (container).enable();
        } else {
          /** @type {{ disable: () => void }} */ (container).disable();
        }
      }
    }
  }
}

if (!customElements.get('product-card-inline-buy')) {
  customElements.define('product-card-inline-buy', ProductCardInlineBuy);
}
